/**
 * Incident + ITASC domain model — pure, I/O-free, shared by web and api.
 *
 * Structured-first: the capture surface is built from these enums (chips /
 * toggles / selects), so the data feeding the rule engine is clean by
 * construction. Free-text `notes` are optional augmentation only.
 */

import type { IncidentStage } from './stages.js';
import { type IncidentClock, createClock } from './clock.js';
import type { ResourceUnit } from './resources.js';
import { type WaterPlan, emptyWaterPlan } from './water.js';
import type { LngLat } from './geo.js';
import {
  type IncidentType,
  type IncidentLevel,
  incidentTypeFromString,
  incidentLevelFromString,
} from './incident-type.js';

// --- Controlled vocabularies -------------------------------------------------

/** Arrival outcomes (blueprint §6.3). Drives confirmed / not-confirmed branching. */
export const CONFIRMATION_STATUSES = [
  'unconfirmed',
  'confirmed-fire',
  'investigating',
  'smoke-only',
  'alarm-operating',
  'good-intent',
  'false-alarm',
  'non-structure-fire',
  'wrong-location',
  'unable-to-access',
] as const;
export type ConfirmationStatus = (typeof CONFIRMATION_STATUSES)[number];

export const CONFIRMATION_LABELS: Record<ConfirmationStatus, string> = {
  unconfirmed: 'Unconfirmed',
  'confirmed-fire': 'Confirmed structure fire',
  investigating: 'Nothing showing — investigating',
  'smoke-only': 'Smoke only',
  'alarm-operating': 'Alarm operating',
  'good-intent': 'Good intent call',
  'false-alarm': 'False alarm',
  'non-structure-fire': 'Burn-off / non-structure fire',
  'wrong-location': 'Wrong location',
  'unable-to-access': 'Unable to access',
};

/** True when the outcome is a confirmed working fire (runs the full checks). */
export const isConfirmedFire = (s: ConfirmationStatus): boolean => s === 'confirmed-fire';

export const SHOWING = ['nothing', 'smoke', 'flame', 'smoke-and-flame'] as const;
export type Showing = (typeof SHOWING)[number];
export const SHOWING_LABELS: Record<Showing, string> = {
  nothing: 'Nothing showing',
  smoke: 'Smoke showing',
  flame: 'Flame showing',
  'smoke-and-flame': 'Smoke and flame showing',
};

export const INTENSITY = ['light', 'moderate', 'severe'] as const;
export type Intensity = (typeof INTENSITY)[number];

export const STRATEGIES = [
  'investigating',
  'first-attack',
  'offensive-internal',
  'offensive-external',
  'defensive',
  'exposure-protection',
  'transitional',
] as const;
export type Strategy = (typeof STRATEGIES)[number];
export const STRATEGY_LABELS: Record<Strategy, string> = {
  investigating: 'Investigating',
  'first-attack': 'First attack',
  'offensive-internal': 'Offensive internal',
  'offensive-external': 'Offensive external',
  defensive: 'Defensive',
  'exposure-protection': 'Exposure protection',
  transitional: 'Transitional',
};

/**
 * Fire status milestones — a generic operational-progress label, not a
 * specific jurisdiction's exact doctrine sequencing (that varies by agency
 * and context), so this is captured as a plain set the officer picks from
 * rather than an implied strict linear order. Distinct from `Strategy`
 * (tactical posture, e.g. offensive/defensive) — this is "how contained is
 * it", relevant once a fire is confirmed, and mainly useful from
 * Suppression onward for handover/SITREP clarity and for reviewing whether
 * still-requested support (water, BA…) remains warranted as status improves.
 */
export const FIRE_STATUS = ['going', 'being-controlled', 'contained', 'under-control', 'safe'] as const;
export type FireStatus = (typeof FIRE_STATUS)[number];
export const FIRE_STATUS_LABELS: Record<FireStatus, string> = {
  going: 'Going',
  'being-controlled': 'Being controlled',
  contained: 'Contained',
  'under-control': 'Under control',
  safe: 'Safe',
};
/** Fire status values that indicate meaningful control has been achieved — used to review whether outstanding support requests are still warranted. */
export const FIRE_STATUS_CONTROLLED: readonly FireStatus[] = ['contained', 'under-control', 'safe'];

export const THREAT_TAGS = [
  'exposures',
  'water',
  'access',
  'utilities',
  'lpg-gas',
  'solar-batteries',
  'asbestos',
  'collapse',
  'smoke',
  'grass-bush-extension',
  // MVA / rescue
  'entrapment',
  'fuel-spill',
  'multiple-vehicles',
  // Hazmat
  'unknown-substance',
  'vapour-cloud',
  // Storm / flood
  'powerlines-down',
  'rising-water',
  // Structural fireground (officer-observed conditions)
  'fire-behaviour-signs',
  // Bush / grass / forest fire
  'ember-attack',
  'spot-fires',
  'wind-change',
  'crown-fire',
  'difficult-terrain',
  'extreme-fire-danger',
  // Vehicle fire / EV / battery
  'ev-hv-battery',
  'stranded-energy',
  'thermal-runaway',
  'reignition-risk',
] as const;
export type ThreatTag = (typeof THREAT_TAGS)[number];

export const ACTION_TAGS = [
  'investigating',
  'size-up-dra',
  'rescue-search',
  'first-attack',
  'offensive-internal',
  'offensive-external',
  'defensive',
  'exposure-protection',
  'establishing-water',
  'overhaul',
  // MVA / rescue
  'extrication',
  'traffic-control',
  // Hazmat
  'decontamination',
  // Storm / flood
  'flood-rescue',
  // Structural fireground
  'ventilation',
  'ba-entry-control',
  'salvage',
  // Bush / grass / forest fire
  'direct-attack',
  'indirect-attack',
  'backburning',
  // Vehicle fire / EV / battery
  'hv-isolation',
  'battery-cooling',
  'battery-monitoring',
] as const;
export type ActionTag = (typeof ACTION_TAGS)[number];

export const SUPPORT_TAGS = [
  'additional-tanker',
  'bulk-water',
  'pumper',
  'structural-operators',
  'ba-operators',
  'ba-cylinders',
  'frnsw',
  'ambulance',
  'police',
  'utilities',
  'duty-officer',
  'fire-investigator',
  'lighting',
  'welfare-rehab',
  // Hazmat
  'hazmat-team',
  'epa',
  // Storm / flood / heavy rescue
  'ses',
  // Bush / grass / forest fire
  'aircraft',
  'raft',
  'npws-forestry',
  // Vehicle fire / EV / battery
  'tow-recovery',
] as const;
export type SupportTag = (typeof SUPPORT_TAGS)[number];

/** Emergency-services acronyms that should render fully upper-case in labels. */
const ACRONYMS = new Set(['ba', 'frnsw', 'rfs', 'lpg', 'dra', 'tic', 'ic', 'eta', 'epa', 'ses', 'mva', 'raft', 'npws']);

/** Shared human labels for tag vocabularies (Title Case, acronym-aware). */
export const labelForTag = (tag: string): string =>
  tag
    .split('-')
    .map((w) => (ACRONYMS.has(w) ? w.toUpperCase() : w[0]!.toUpperCase() + w.slice(1)))
    .join(' ');

// --- ITASC -------------------------------------------------------------------

export interface Itasc {
  incident: {
    confirmation: ConfirmationStatus;
    incidentType?: string;
    buildingType?: string;
    size?: string;
    intensity?: Intensity;
    showing?: Showing;
    /** Operational fire-status milestone (going/contained/under control/safe) — relevant once confirmed as a working fire. */
    fireStatus?: FireStatus;
    locationConfirmed: boolean;
  };
  threats: {
    /** Life-risk read, e.g. "Occupancy unknown". */
    life?: string;
    selected: ThreatTag[];
    notes?: string;
  };
  actions: {
    strategy?: Strategy;
    selected: ActionTag[];
    notes?: string;
  };
  support: {
    requested: SupportTag[];
    unresolvedGaps: SupportTag[];
    notes?: string;
  };
  command: {
    incidentName?: string;
    controlTaken: boolean;
    incidentController?: string;
    radioChannel?: string;
    staging?: string;
  };
}

export function emptyItasc(): Itasc {
  return {
    incident: { confirmation: 'unconfirmed', locationConfirmed: false },
    threats: { selected: [] },
    actions: { selected: [] },
    support: { requested: [], unresolvedGaps: [] },
    command: { controlTaken: false },
  };
}

// --- Incident ----------------------------------------------------------------

export interface Incident {
  id: string;
  name: string;
  typeReported: IncidentType;
  typeConfirmed?: IncidentType;
  /**
   * Other incident types currently believed to be simultaneously active
   * (e.g. a vehicle fire that has spread into an adjacent paddock is both
   * `VehicleFire` and `GrassFire` at once) — never displaces the primary
   * type (`typeConfirmed ?? typeReported`), only adds to it. Type-agnostic
   * doctrine/rules (e.g. water supply) already apply regardless via
   * `applicability.incidentTypes` being empty/undefined; this array is for
   * type-specific doctrine/rules/ITASC vocabulary from more than one type
   * needing to apply at once. See `activeIncidentTypes()`.
   */
  additionalTypes: IncidentType[];
  status: ConfirmationStatus;
  stage: IncidentStage;
  level: IncidentLevel;
  location: string;
  /** Geocoded incident point (set from a seeded locality for the POC). */
  coordinates?: LngLat;
  /** Locality key referencing a seeded spatial dataset (e.g. "bungendore"). */
  locality?: string;
  leadAgency: string;
  supportingAgencies: string[];
  clock: IncidentClock;
  createdAt: string;
  itasc: Itasc;
  /** Responding units (multi-appliance, multi-brigade). */
  resources: ResourceUnit[];
  waterPlan: WaterPlan;
  /** Schema marker so persisted incidents can be migrated. */
  schemaVersion: 2;
}

export interface CreateIncidentInput {
  id?: string;
  name: string;
  typeReported?: IncidentType;
  location: string;
  coordinates?: LngLat;
  locality?: string;
  level?: IncidentLevel;
  leadAgency?: string;
  supportingAgencies?: string[];
  stage?: IncidentStage;
  startedAt?: string;
}

function newId(): string {
  return `inc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function createIncident(input: CreateIncidentInput): Incident {
  const startedAt = input.startedAt ?? new Date().toISOString();
  return {
    id: input.id ?? newId(),
    name: input.name,
    typeReported: input.typeReported ?? 'StructureFire',
    status: 'unconfirmed',
    stage: input.stage ?? 'Dispatch',
    level: input.level ?? 'Level1',
    location: input.location,
    coordinates: input.coordinates,
    locality: input.locality,
    leadAgency: input.leadAgency ?? 'NSW RFS',
    supportingAgencies: input.supportingAgencies ?? ['FRNSW'],
    clock: createClock(startedAt),
    createdAt: startedAt,
    itasc: emptyItasc(),
    resources: [],
    waterPlan: emptyWaterPlan(),
    additionalTypes: [],
    schemaVersion: 2,
  };
}

/**
 * Backfill an `itasc` object against the current shape, field by field, rather
 * than trusting a persisted incident's `itasc` to already match it. A persisted
 * incident predates every ITASC field added since it was last saved (`fireStatus`,
 * `unresolvedGaps`, etc.) and the rule engine (`insights/rules.ts`) reads
 * `threats.selected` / `actions.selected` / `support.requested` unguarded — a
 * missing array there throws inside a `useMemo` with no enclosing error boundary,
 * which blanks the whole app rather than just this one incident.
 */
function migrateItasc(raw: unknown): Itasc {
  const empty = emptyItasc();
  const r = (typeof raw === 'object' && raw !== null ? raw : {}) as Partial<Itasc>;
  return {
    incident: { ...empty.incident, ...r.incident },
    threats: { ...empty.threats, ...r.threats, selected: Array.isArray(r.threats?.selected) ? r.threats.selected : empty.threats.selected },
    actions: { ...empty.actions, ...r.actions, selected: Array.isArray(r.actions?.selected) ? r.actions.selected : empty.actions.selected },
    support: {
      ...empty.support,
      ...r.support,
      requested: Array.isArray(r.support?.requested) ? r.support.requested : empty.support.requested,
      unresolvedGaps: Array.isArray(r.support?.unresolvedGaps) ? r.support.unresolvedGaps : empty.support.unresolvedGaps,
    },
    command: { ...empty.command, ...r.command },
  };
}

/** Backfill fields added in later schema versions onto a loaded incident. */
export function migrateIncident(raw: Partial<Incident> & { schemaVersion?: number }): Incident {
  const rawAny = raw as Record<string, unknown>;
  return {
    ...(raw as Incident),
    // Epic 11: coerce legacy free-text type/level strings to typed enum values.
    typeReported: incidentTypeFromString((rawAny['typeReported'] as string | undefined) ?? 'StructureFire'),
    typeConfirmed: rawAny['typeConfirmed'] != null
      ? incidentTypeFromString(rawAny['typeConfirmed'] as string)
      : undefined,
    // Incident-type-switching: backfill missing/legacy additionalTypes, coercing
    // each entry the same way and dropping anything invalid rather than throwing.
    additionalTypes: Array.isArray(rawAny['additionalTypes'])
      ? Array.from(new Set((rawAny['additionalTypes'] as unknown[]).map((t) => incidentTypeFromString(String(t)))))
      : [],
    level: incidentLevelFromString((rawAny['level'] as string | undefined) ?? 'Level1'),
    resources: raw.resources ?? [],
    waterPlan: raw.waterPlan ?? emptyWaterPlan(),
    itasc: migrateItasc(rawAny['itasc']),
    schemaVersion: 2,
  };
}

/**
 * All incident types currently believed active — the primary (confirmed, or
 * reported if not yet confirmed) type first, followed by any co-active
 * `additionalTypes`, deduplicated. This is the set rule/doctrine matching and
 * the ITASC/graph views should consult, not `typeReported` alone, so that an
 * evolving incident (a car fire found to be spreading into grass, a "bush
 * alight" job that turns out to be a hidden structure) keeps every relevant
 * type's rules/tags/doctrine live at once rather than only the type recorded
 * at creation.
 */
export function activeIncidentTypes(incident: Incident): IncidentType[] {
  const primary = incident.typeConfirmed ?? incident.typeReported;
  // Defensive against un-migrated/partial data crossing an I/O boundary
  // (e.g. a request body) rather than going through migrateIncident().
  return Array.from(new Set([primary, ...(incident.additionalTypes ?? [])]));
}

/**
 * Reclassify the incident's primary type (e.g. "this is actually a structure
 * fire, not an alarm"). `typeReported` — the original call classification —
 * is never mutated, preserving the "what was it first thought to be" history;
 * only `typeConfirmed` changes. If `type` was already tracked as an
 * additional (co-active) type, it's removed from there so it isn't listed
 * twice.
 */
export function setPrimaryType(incident: Incident, type: IncidentType): Incident {
  return {
    ...incident,
    typeConfirmed: type,
    additionalTypes: incident.additionalTypes.filter((t) => t !== type),
  };
}

/**
 * Mark another incident type as simultaneously active alongside the primary
 * type (e.g. a vehicle fire that has spread into an adjacent paddock also
 * becomes a grass fire) — never displaces the primary type or any rules/tags
 * already in play for it.
 */
export function addIncidentType(incident: Incident, type: IncidentType): Incident {
  const primary = incident.typeConfirmed ?? incident.typeReported;
  if (type === primary || incident.additionalTypes.includes(type)) return incident;
  return { ...incident, additionalTypes: [...incident.additionalTypes, type] };
}

/**
 * Stop treating `type` as a simultaneously-active additional type (e.g. the
 * grass-fire component has since been fully contained and is no longer
 * operationally relevant). The primary type can't be removed this way — use
 * `setPrimaryType` to reclassify it instead.
 */
export function removeIncidentType(incident: Incident, type: IncidentType): Incident {
  return { ...incident, additionalTypes: incident.additionalTypes.filter((t) => t !== type) };
}

// --- Pure update helpers (used by the web store and, later, the api) ---------

export function setStage(incident: Incident, stage: IncidentStage): Incident {
  return { ...incident, stage };
}

export function setClock(incident: Incident, clock: IncidentClock): Incident {
  return { ...incident, clock };
}

/**
 * Set the confirmation outcome; keeps incident.status and the ITASC incident
 * block in step, and promotes a confirmed type.
 */
export function setConfirmation(incident: Incident, status: ConfirmationStatus): Incident {
  return {
    ...incident,
    status,
    typeConfirmed: isConfirmedFire(status)
      ? incident.typeConfirmed ?? incident.typeReported
      : incident.typeConfirmed,
    itasc: {
      ...incident.itasc,
      incident: { ...incident.itasc.incident, confirmation: status },
    },
  };
}

/** Deep-merge a partial ITASC patch over the current ITASC. */
export function updateItasc(incident: Incident, patch: DeepPartialItasc): Incident {
  return { ...incident, itasc: mergeItasc(incident.itasc, patch) };
}

export type DeepPartialItasc = {
  [K in keyof Itasc]?: Partial<Itasc[K]>;
};

function mergeItasc(base: Itasc, patch: DeepPartialItasc): Itasc {
  return {
    incident: { ...base.incident, ...patch.incident },
    threats: { ...base.threats, ...patch.threats },
    actions: { ...base.actions, ...patch.actions },
    support: { ...base.support, ...patch.support },
    command: { ...base.command, ...patch.command },
  };
}

/** Toggle a tag within a string-array field (immutable). */
export function toggleInArray<T>(arr: readonly T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

// --- resources & water -------------------------------------------------------

export function addUnit(incident: Incident, unit: ResourceUnit): Incident {
  return { ...incident, resources: [...incident.resources, unit] };
}

export function updateUnit(
  incident: Incident,
  unitId: string,
  patch: Partial<ResourceUnit>,
): Incident {
  return {
    ...incident,
    resources: incident.resources.map((u) => (u.id === unitId ? { ...u, ...patch } : u)),
  };
}

export function removeUnit(incident: Incident, unitId: string): Incident {
  return { ...incident, resources: incident.resources.filter((u) => u.id !== unitId) };
}

export function updateWaterPlan(incident: Incident, patch: Partial<WaterPlan>): Incident {
  return { ...incident, waterPlan: { ...incident.waterPlan, ...patch } };
}
